import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import classNames from 'classnames'
import { LoadingSpinnerIcon } from '@/atoms/Icons/LoadingSpinnerIcon'
import { paths } from '@/constants/paths'
import { slugs } from '@/constants/slugs'
import { ThemeContextProvider } from '@/contexts/ThemeContext'
import { ServerExperiments } from '@/experimentation'
import { useDisclose } from '@/hooks/useDisclose'
import { Seo } from '@/layout/Seo'
import { useMobileNavHiddenState } from '@/layouts/TicketsLayout/MobileNavHiddenStateProvider'
import { ShareGuildTicketsModal } from '@/organisms/ShareGuildTicketsModal/ShareGuildTickets'
import { GiveawaySchedule, TheatricalReleaseResult, ValidPublicDiscounts } from '@/services/Theatrical/queries'
import { getItemOrFirstEntry } from '@/utils/array-utils'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { useTranslate } from '@/utils/translate/translate-client'
import useHydration from '@/utils/useHydration'
import { OverlayYoutubeVideo } from '@/views/PreSales/PreSalesGiveawayView/components/Modals/OverlayYouTubeVideo'
import { ShowtimeDetailsPanel } from '@/views/TicketCheckoutViews/ShowtimesView/components/ShowtimeDetailsPanel'
import { ShowtimesLayout } from '@/views/TicketCheckoutViews/ShowtimesView/components/ShowtimesLayout'
import { TicketsContextProvider } from '@/views/TicketCheckoutViews/ShowtimesView/components/TicketsContext'
import { TicketsPromoContextProvider } from '@/views/TicketCheckoutViews/ShowtimesView/components/TicketsPromo/TicketsPromoContext'
import TicketDiscountCodesModal from '@/views/TicketCheckoutViews/ShowtimesView/components/modals/TicketDiscountsModal'
import { useModalRefundErrorHandler } from '@/views/TicketCheckoutViews/ShowtimesView/hooks/useModalRefundErrorHandler'
import { ErrorRefundedModal } from '@/views/TicketCheckoutViews/components/ErrorRefundModal'
import { TheatricalPromotionContextProvider } from '@/views/TicketCheckoutViews/components/TheatricalPromotionContext'
import { Flow, getCodes, getDiscountCodes } from '@/views/TicketCheckoutViews/utils'
import { TheatricalPromotion } from '../components/TheatricalPromotionContext/types'
import { KidsGoFreeProgressBanner } from './components/KidsGoFreeProgressBanner'
import { InitializedReservation } from './initReservation'
import { useSeoData } from './useSeoData'

interface Props {
  theatricalRelease: TheatricalReleaseResult
  theatricalSlug: string
  projectGuildScore?: number | null
  shouldUseDataDog?: boolean
  isEditReservationFlow?: boolean
  isSceneModal?: boolean
  experiments?: ServerExperiments
  reservation: InitializedReservation
  publicDiscounts?: ValidPublicDiscounts
  giveawaySchedule?: GiveawaySchedule | null
  promo?: TheatricalPromotion | null
  basicPageExperimentEnabled?: boolean
}

export const ShowtimesView: FC<Props> = ({
  theatricalRelease,
  theatricalSlug,
  isEditReservationFlow = false,
  projectGuildScore,
  isSceneModal,
  experiments,
  reservation,
  publicDiscounts,
  giveawaySchedule,
  promo,
  basicPageExperimentEnabled,
}) => {
  const [isShareGuildCodesModalOpen, setIsShareGuildCodesModalOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const calendarRef = useRef<HTMLDivElement>(null)
  const { query, asPath, isReady, push } = useSafeRouter()
  const sceneId = getItemOrFirstEntry(query?.sceneId)
  const { t } = useTranslate('tickets')

  const { displayErrorRefundModal, handleCloseErrorRefundModal } = useModalRefundErrorHandler()
  const { isOpen: isScenePlayerOpen, onClose: onCloseScenePlayer } = useDisclose(true)

  const shouldEmergencyRedirectToFandango =
    theatricalRelease?.region?.ticketsEmergencyRedirectUrl && theatricalRelease?.region?.redirectTicketsPage
  const seoData = useSeoData(theatricalRelease, asPath)
  const flow = getItemOrFirstEntry(query.flow)
  const isGuildShare = flow === Flow.GUILD_SHARE
  const isGiftFlow = flow === Flow.GUILD_GIFT || flow === Flow.GIFT_TICKETS
  const isGiftTickets = flow === Flow.GIFT_GUILD_TICKETS

  const scrollToTop = useCallback(() => {
    calendarRef?.current?.scrollIntoView({ behavior: 'smooth' })
  }, [])

  const discountCodes = useMemo(() => getDiscountCodes({ query }), [query])
  const codes = useMemo(() => getCodes({ query }), [query])

  const { isMobileNavHidden } = useMobileNavHiddenState()
  const { isHydrated } = useHydration()

  useEffect(() => {
    if (shouldEmergencyRedirectToFandango && isReady) {
      push(theatricalRelease?.region?.ticketsEmergencyRedirectUrl as string)
    }
  }, [isReady, push, shouldEmergencyRedirectToFandango, theatricalRelease?.region?.ticketsEmergencyRedirectUrl])

  useEffect(() => {
    if (isGuildShare && discountCodes?.length > 0) {
      setIsShareGuildCodesModalOpen(true)
    }
  }, [discountCodes?.length, isGuildShare, flow])

  useEffect(() => {
    if (isGiftTickets && discountCodes?.length > 0) {
      setIsShareGuildCodesModalOpen(true)
    }
  }, [discountCodes?.length, isGiftTickets, flow])

  if (shouldEmergencyRedirectToFandango)
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingSpinnerIcon size={40} />
      </div>
    )

  if (!theatricalRelease) return null

  return (
    <>
      <Seo
        path={`${paths.tickets.index}/${theatricalSlug}`}
        canonical={seoData.url}
        description={seoData.description}
        openGraph={seoData}
        title={seoData.title}
      />
      <TicketsContextProvider
        isEditReservationFlow={isEditReservationFlow}
        theatricalRelease={theatricalRelease}
        defaultSearchDate={new Date()}
        experiments={experiments}
        basicPageExperimentEnabled={basicPageExperimentEnabled}
        projectGuildScore={projectGuildScore}
      >
        <TicketsPromoContextProvider theatricalSlug={theatricalSlug}>
          <TheatricalPromotionContextProvider
            theatricalSlug={theatricalSlug}
            experiments={experiments}
            publicDiscounts={publicDiscounts ?? { earlyBirdDiscount: null, noFeesDiscount: null }}
            giveawaySchedule={giveawaySchedule ?? null}
            promo={promo ?? null}
          >
            <ThemeContextProvider isDarkMode={false}>
              <div
                ref={containerRef}
                className={classNames('fixed inset-0 overflow-x-hidden md:top-16 md:h-[calc(100dvh-64px)]', {
                  'top-16 h-[calc(100dvh-64px)]': isHydrated && !isMobileNavHidden,
                  'theatrical-experiment': basicPageExperimentEnabled,
                })}
              >
                {!isEditReservationFlow && (
                  <>
                    <ShowtimeDetailsPanel
                      className="experiment-hidden"
                      containerRef={containerRef}
                      scrollToTop={scrollToTop}
                      theatricalSlug={theatricalSlug}
                      theatricalRelease={theatricalRelease}
                      isSceneModal={isSceneModal}
                      projectGuildScore={projectGuildScore}
                    />
                    {theatricalSlug === slugs.david && (
                      <KidsGoFreeProgressBanner
                        className="experiment-hidden lg:hidden"
                        theatricalSlug={theatricalSlug}
                        releaseDate={theatricalRelease.region?.releaseDate}
                      />
                    )}
                  </>
                )}

                {basicPageExperimentEnabled && sceneId && (
                  <OverlayYoutubeVideo
                    youtubeID={sceneId}
                    isOpen={isScenePlayerOpen}
                    onClose={onCloseScenePlayer}
                    buttonText={t('getShowtimes', 'Get Showtimes')}
                  />
                )}

                <div
                  className={classNames(
                    'bottom-0 flex flex-col lg:absolute lg:left-[476px] lg:right-0 lg:top-0',
                    isEditReservationFlow && '!left-0',
                    basicPageExperimentEnabled && 'lg:!left-0',
                  )}
                >
                  <ShowtimesLayout
                    theatricalRelease={theatricalRelease}
                    reservation={reservation}
                    theatricalSlug={theatricalSlug}
                    calendarRef={calendarRef}
                    scrollToTop={scrollToTop}
                    isEditReservationFlow={isEditReservationFlow}
                  />
                </div>
                {discountCodes?.length > 0 && !isGuildShare && isGiftFlow && (
                  <TicketDiscountCodesModal discounts={discountCodes} isGiftFlow={isGiftFlow} />
                )}
                {codes?.length > 0 && <TicketDiscountCodesModal isAutoApplied={false} discounts={codes} />}
                <ShareGuildTicketsModal
                  open={isShareGuildCodesModalOpen}
                  onClose={() => {
                    setIsShareGuildCodesModalOpen(false)
                  }}
                  ModalStep={isGiftTickets ? 'gift-confirmation' : 'main'}
                  theatricalSlug={theatricalSlug}
                  projectName={theatricalRelease?.title as string}
                  source={'default'}
                />

                <ErrorRefundedModal isOpen={displayErrorRefundModal} onClose={handleCloseErrorRefundModal} />
              </div>
            </ThemeContextProvider>
          </TheatricalPromotionContextProvider>
        </TicketsPromoContextProvider>
      </TicketsContextProvider>
    </>
  )
}
